// FILE: src/pages/FamilySignupPage.jsx
import React, { useState } from "react";

import TurnstileWidget from "../components/TurnstileWidget";
import { doc, getDoc } from "firebase/firestore";
import { createUserWithEmailAndPassword, getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { completeBusinessSignup } from "../lib/businessSignupApi";
import { createTenantSubscriptionCheckout } from "../lib/tenantBillingApi";

const B = {
  red: "#980000", redDark: "#650202", orange: "#f15822",
  navy: "#003353", steel: "#657899", bg: "#f6f7fb",
  white: "#ffffff", border: "#e8ebf3",
};

const inputStyle = {
  width: "100%", height: 44, padding: "0 14px",
  borderRadius: 10, border: `1px solid ${B.border}`,
  fontSize: 15, boxSizing: "border-box", background: B.white,
};

const labelStyle = {
  display: "block", fontSize: 13, fontWeight: 700,
  color: B.steel, marginBottom: 6,
};

export default function FamilySignupPage() {
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
  const [form, setForm] = useState({
    familyName: "",
    ownerName: "",
    email: "",
    password: "",
    confirmPassword: "",
    agreed: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");



  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    if (!form.familyName.trim()) return setErr("Please enter a family name.");
    if (!form.ownerName.trim()) return setErr("Please enter your full name.");
    if (!form.email.trim()) return setErr("Please enter your email.");
    if (form.password.length < 8) return setErr("Password must be at least 8 characters.");
    if (form.password !== form.confirmPassword) return setErr("Passwords do not match.");
    if (!form.agreed) return setErr("Please agree to the Terms & Conditions.");

    if (turnstileSiteKey && !turnstileToken) {
      setErr("Please complete the security check.");
      return;
    }
    setBusy(true);
    try {
      const auth = getAuth();
      window.__lwb_signup_in_progress = true;
      await createUserWithEmailAndPassword(auth, form.email.trim().toLowerCase(), form.password);

      const result = await completeBusinessSignup({
        companyName: `${form.familyName.trim()} Family`,
        fullName: form.ownerName.trim(),
        email: form.email.trim().toLowerCase(),
        planKey: "family",
        agreedToTerms: true,
        turnstileToken,
      });

      if (!result?.tenantId) throw new Error("Family account creation failed");

      sessionStorage.setItem("lwb_business_signup_tenant_id", result.tenantId);
      sessionStorage.setItem("lwb_business_signup_plan_key", "family");
      sessionStorage.setItem("lwb_business_signup_checkout_pending", "1");

      const checkout = await createTenantSubscriptionCheckout(result.tenantId, "family");
      if (!checkout?.url) throw new Error("Account created but checkout could not be started.");

      window.location.replace(checkout.url);
    } catch (e2) {
      window.__lwb_signup_in_progress = false;
      if (e2?.code === "auth/email-already-in-use") {
        // Try to recover — sign them in and resume checkout if pending
        // Keep busy=true and suppress auth redirect during resume
        window.__lwb_signup_in_progress = true;
        try {
          const auth = getAuth();
          const cred = await signInWithEmailAndPassword(auth, form.email.trim().toLowerCase(), form.password);
          const uid = cred.user.uid;
          // Find their tenant
          const { db: fireDb } = await import("../firebase");
          const userSnap = await getDoc(doc(fireDb, "users", uid));
          const userData = userSnap.exists() ? userSnap.data() : {};
          const tenantId = userData.primaryTenantId || userData.tenantId || "";
          if (tenantId) {
            const tenantSnap = await getDoc(doc(fireDb, "tenants", tenantId));
            const tenantData = tenantSnap.exists() ? tenantSnap.data() : {};
            if (tenantData.billingStatus === "pending_checkout") {
              // Resume checkout — stay on loading screen until redirect
              const checkout = await createTenantSubscriptionCheckout(tenantId, "family");
              if (checkout?.url) {
                window.location.replace(checkout.url);
                return;
              }
            }
          }
          window.__lwb_signup_in_progress = false;
          setErr("You already have an account. Please log in to manage your subscription.");
        } catch (resumeErr) {
          window.__lwb_signup_in_progress = false;
          if (resumeErr?.code === "auth/wrong-password" || resumeErr?.code === "auth/invalid-credential") {
            setErr("An account with this email already exists but the password is incorrect. Please check your password or reset it below.");
          } else {
            setErr("An account with this email already exists. Please log in or use a different email.");
          }
        }
        setBusy(false);
        return;
      }
      setErr(e2.message || "Signup failed. Please try again.");
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: B.bg, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {/* Full screen loading state */}
      {busy && (
        <div style={{ position: "fixed", inset: 0, background: "#fff", zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 24 }}>
          <img src="/Lastwishbox-logo.png" alt="LastWish Box" style={{ height: 52, width: "auto" }} />
          <div style={{ textAlign: "center" }}>
            <h2 style={{ margin: "0 0 8px", color: "#650202", fontSize: 22, fontWeight: 800 }}>Setting up your family account</h2>
            <p style={{ margin: 0, color: "#657899", fontSize: 15 }}>Please wait while we prepare your secure checkout…</p>
          </div>
          <div style={{ width: 48, height: 48, border: "4px solid #f0e0e0", borderTop: "4px solid #980000", borderRadius: "50%", animation: "lwb-spin 0.8s linear infinite" }} />
          <style>{`@keyframes lwb-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Nav */}
      <div style={{ background: B.white, borderBottom: `1px solid ${B.border}`, padding: "12px 20px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <a href="/"><img src="/Lastwishbox-logo.png" alt="LastWish Box" style={{ height: 36, width: "auto" }} /></a>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <a href="/personal/plans" style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${B.border}`, background: B.white, color: B.redDark, fontWeight: 700, fontSize: 13, textDecoration: "none", whiteSpace: "nowrap" }}>← Back to plans</a>
            <a href="/login" style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${B.redDark}`, background: B.redDark, color: B.white, fontWeight: 700, fontSize: 13, textDecoration: "none", whiteSpace: "nowrap" }}>Log in</a>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 20px 80px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 40, alignItems: "start" }}>

        {/* Left — plan summary */}
        <div>
          <div style={{ display: "inline-block", background: "#fff0ee", color: B.red, borderRadius: 999, padding: "6px 16px", fontSize: 13, fontWeight: 700, marginBottom: 16, border: `1px solid ${B.orange}` }}>Family Plan</div>
          <h1 style={{ margin: "0 0 12px", color: B.redDark, fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, lineHeight: 1.1 }}>One plan for the whole family</h1>
          <p style={{ color: B.steel, fontSize: 15, lineHeight: 1.7, margin: "0 0 28px" }}>Each family member gets their own private, secure account. Store wishes, messages, documents and memories — safely and independently.</p>

          <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 16, padding: "24px 20px", boxShadow: "0 4px 16px rgba(0,0,0,0.04)" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 20 }}>
              <span style={{ fontSize: 40, fontWeight: 900, color: B.redDark }}>€199.99</span>
              <span style={{ fontSize: 14, color: B.steel, fontWeight: 600 }}>/year</span>
            </div>
            {[
              "Up to 5 family members",
              "Each member gets their own private account",
              "3 account managers per member",
              "5 GB secure storage per member",
              "Full digital vault & messaging",
              "1 family organiser manages invites",
              "Priority support",
            ].map((f, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
                <span style={{ width: 20, height: 20, borderRadius: 6, background: "#fff0ee", border: `1px solid ${B.orange}`, display: "inline-flex", alignItems: "center", justifyContent: "center", color: B.red, fontWeight: 900, fontSize: 11, flexShrink: 0, marginTop: 1 }}>✓</span>
                <span style={{ fontSize: 14, color: "#374151" }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right — signup form */}
        <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 20, boxShadow: "0 8px 32px rgba(42,5,22,0.08)", overflow: "hidden" }}>
          <div style={{ background: B.redDark, padding: "24px 28px" }}>
            <h2 style={{ margin: 0, color: B.white, fontSize: 20, fontWeight: 800 }}>Set up your family account</h2>
            <p style={{ margin: "6px 0 0", color: "rgba(255,255,255,0.75)", fontSize: 14 }}>You'll manage invites for your family members after signup.</p>
          </div>
          <div style={{ padding: "24px 28px 32px" }}>
            {err && (
              <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", color: "#7f1d1d", fontSize: 13, marginBottom: 16 }}>
                {err}
                {(err.includes("password is incorrect") || err.includes("wrong-password")) && (
                  <div style={{ marginTop: 8 }}>
                    <a href="/reset-password" style={{ color: "#980000", fontWeight: 700, textDecoration: "underline" }}>
                      Reset your password →
                    </a>
                  </div>
                )}
              </div>
            )}

            <label style={labelStyle}>Family name</label>
            <input style={{ ...inputStyle, marginBottom: 16 }} placeholder="e.g. The Murphy Family" value={form.familyName} onChange={e => setField("familyName", e.target.value)} />

            <label style={labelStyle}>Your full name</label>
            <input style={{ ...inputStyle, marginBottom: 16 }} placeholder="Jane Murphy" value={form.ownerName} onChange={e => setField("ownerName", e.target.value)} />

            <label style={labelStyle}>Your email</label>
            <input style={{ ...inputStyle, marginBottom: 4 }} type="email" placeholder="jane@example.com" value={form.email} onChange={e => setField("email", e.target.value)} />
            <p style={{ fontSize: 12, color: B.steel, marginBottom: 16 }}>This will be the main organiser account.</p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Password</label>
                <input style={inputStyle} type="password" placeholder="Min. 8 characters" value={form.password} onChange={e => setField("password", e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Confirm password</label>
                <input style={inputStyle} type="password" placeholder="Re-enter password" value={form.confirmPassword} onChange={e => setField("confirmPassword", e.target.value)} />
              </div>
            </div>

            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 20, cursor: "pointer" }}>
              <input type="checkbox" checked={form.agreed} onChange={e => setField("agreed", e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: B.steel }}>I have read and agree to the <a href="https://lastwishbox.com/policies/" target="_blank" rel="noopener noreferrer" style={{ color: B.red, fontWeight: 700 }}>Terms &amp; Conditions</a> and <a href="https://lastwishbox.com/policies/" target="_blank" rel="noopener noreferrer" style={{ color: B.red, fontWeight: 700 }}>Privacy Policy</a>.</span>
            </label>

            {turnstileSiteKey && <TurnstileWidget siteKey={turnstileSiteKey} onVerify={setTurnstileToken} />}

            <button onClick={handleSubmit} disabled={busy} style={{ width: "100%", height: 50, borderRadius: 12, border: "none", background: B.red, color: B.white, fontWeight: 800, fontSize: 16, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1 }}>
              {busy ? "Setting up your family account…" : "Continue to payment →"}
            </button>

            <p style={{ fontSize: 12, color: B.steel, textAlign: "center", marginTop: 14, lineHeight: 1.6 }}>
              You'll be taken to Stripe to complete payment. No hidden fees. Cancel anytime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
